import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { getCurrentAppIdentifier } from '../config/apps'
import api, { setAuthHandlers } from '../services/api'
import { getTrustedDeviceToken } from '../services/otpApi'
import type { User, Membership } from '../types'
import {
  resolveActiveMembership as resolveActiveMembershipPure,
  needsOrgChoice,
  pickPerTabOrgOverride,
} from './activeOrg'

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  activeOrganizationId: string | null
  memberships: Membership[]
  /** Há mais de uma org compatível com o portal e a aba ainda não escolheu (#1145). Não persistido. */
  orgChoiceRequired: boolean
}

interface RegisterData {
  user: {
    first_name: string
    last_name: string
    email: string
    password: string
    phone: string
  }
  organization: {
    name: string
    document: string
    size: string
    industry: string
    type: string
  }
  address: {
    street: string
    city: string
    state: string
    zip_code: string
    country: string
  }
  preferences: {
    marketing_emails: boolean
  }
}

export class OTPRequiredError extends Error {
  requires_otp = true
  temp_token: string
  declare message: string
  method: string // 'totp' (app) | 'email'
  email_fallback: boolean // se o usuário pode receber código por email como alternativa

  constructor(
    temp_token: string,
    message: string,
    method: string = 'email',
    emailFallback: boolean = false,
  ) {
    super(message)
    this.temp_token = temp_token
    this.name = 'OTPRequiredError'
    this.method = method
    this.email_fallback = emailFallback
  }
}

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<void>
  completeOTPLogin: (accessToken: string, refreshToken?: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  updateUser: (user: User) => void
  setLoading: (loading: boolean) => void
  checkAuth: () => Promise<void>
  setActiveOrganization: (orgId: string) => void
  /** Escolha feita na tela "Em qual organização você quer atuar?" */
  chooseOrganization: (orgId: string) => Promise<void>
  setTokens: (accessToken: string, refreshToken?: string) => void
}

/**
 * Qual portal este bundle é — do CONTRATO DE DEPLOY (#996).
 *
 * Havia aqui um mapa `hostname -> app` com treze hosts de produção cravados, seguido de
 * um mapa `porta -> app` e de `'unknown'` como último recurso. Duas consequências, além
 * de o bundle de staging carregar os hosts de produção: o portal white-label de uma
 * revenda em domínio PRÓPRIO caía em `'unknown'` e guardava sessão sob
 * `nebvla-auth-unknown`; e a mesma pergunta era respondida por três cópias divergentes
 * da régua (aqui, em `config/apps.ts` e dentro de dois componentes do EC).
 *
 * O bundle não precisa adivinhar o que ele é: foi buildado como um portal específico.
 */
const APP_IDENTIFIER: string = getCurrentAppIdentifier()

// --- Org ativa por aba (isolamento entre abas do mesmo portal) ---
// A org ativa não é mais compartilhada via localStorage: cada aba mantém a sua
// em sessionStorage. Prioridade na resolução: ?org= (nova aba) > sessionStorage
// (esta aba, sobrevive a F5) > findBestMembership (uma só org compatível). Com mais de uma e sem sinal, a tela de escolha decide.
const SESSION_ORG_KEY = `nebvla-active-org-${APP_IDENTIFIER}`

// Lê ?org=<id> da URL atual (sinal explícito ao abrir uma org em nova aba)
const readOrgFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('org')
  } catch {
    return null
  }
}

// Remove ?org= da URL, sem recarregar, depois de aplicado
const stripOrgFromUrl = (): void => {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has('org')) {
      url.searchParams.delete('org')
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
    }
  } catch {
    /* noop */
  }
}

const readSessionActiveOrg = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_ORG_KEY)
  } catch {
    return null
  }
}

const writeSessionActiveOrg = (orgId: string | null): void => {
  try {
    if (orgId) {
      sessionStorage.setItem(SESSION_ORG_KEY, orgId)
    } else {
      sessionStorage.removeItem(SESSION_ORG_KEY)
    }
  } catch {
    /* noop */
  }
}

// Org ativa inicial (síncrona), para o header X-Organization-Id já sair correto
// antes do checkAuth resolver as memberships. Só sinais explícitos da aba: o blob
// legado do localStorage NÃO é escolha automática (#1145, sem "lembrar minha escolha").
const readInitialActiveOrg = (): string | null => readOrgFromUrl() || readSessionActiveOrg()

// Resolve a membership ativa lendo os sinais do DOM/storage desta aba e
// delegando a prioridade para a lógica pura em activeOrg.ts
const resolveActiveMembership = (
  memberships: Membership[],
  fallbackId?: string | null,
): Membership | undefined =>
  resolveActiveMembershipPure(memberships, {
    urlOrg: readOrgFromUrl(),
    sessionOrg: readSessionActiveOrg(),
    fallbackId,
    appIdentifier: APP_IDENTIFIER,
  })

const readPerTabSignals = () => ({
  urlOrg: readOrgFromUrl(),
  sessionOrg: readSessionActiveOrg(),
  appIdentifier: APP_IDENTIFIER,
})

/**
 * Sessão derivada de um /auth/me: resolve a org ativa OU marca que a escolha é do
 * usuário (`orgChoiceRequired`). Nesse caso não há org ativa — nenhum header
 * X-Organization-Id sai, e o backend também recusaria adivinhar (409).
 */
const buildSession = (
  me: any,
  memberships: Membership[],
  fallbackId?: string | null,
): {
  user: User
  activeOrgId: string | null
  orgChoiceRequired: boolean
} => {
  const orgChoiceRequired = needsOrgChoice(memberships, readPerTabSignals())
  const active = orgChoiceRequired ? undefined : resolveActiveMembership(memberships, fallbackId)
  const activeOrgId = orgChoiceRequired
    ? null
    : active?.organization_id || me.organization_id || null
  const user = {
    ...me,
    organization_id: activeOrgId,
    organization: orgChoiceRequired
      ? undefined
      : {
          id: activeOrgId,
          name: active?.organization_name || me.organization_name,
          type: active?.organization_type || me.organization_type,
          parent_id: active?.parent_id || me.parent_id,
          parent_name: active?.parent_name || me.parent_name,
        },
  } as User
  return { user, activeOrgId, orgChoiceRequired }
}

/**
 * Lê o /auth/me e monta a sessão. Se a org ativa saiu de um SINAL da aba ou da regra de
 * uma só compatível, mas o /me veio sem header (topo vazio com mais de uma membership),
 * relê com o X-Organization-Id: roles/permissions/organization_* do topo são da org ativa.
 */
const carregarSessao = async (token: string, fallbackId?: string | null) => {
  const ler = (orgId?: string | null) => {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    if (orgId) headers['X-Organization-Id'] = orgId
    return api.get('/api/v1/auth/me', { headers })
  }
  let me = (await ler(fallbackId)).data
  let memberships: Membership[] = me.memberships || []
  let sessao = buildSession(me, memberships, fallbackId)
  if (
    !sessao.orgChoiceRequired &&
    sessao.activeOrgId &&
    me.organization_id !== sessao.activeOrgId
  ) {
    me = (await ler(sessao.activeOrgId)).data
    memberships = me.memberships || memberships
    sessao = buildSession(me, memberships, fallbackId)
  }
  return { ...sessao, memberships }
}

// Limpar tokens antigos sem identificador que podem estar causando conflito
const cleanupOldTokens = () => {
  const currentKey = `nebvla-auth-${APP_IDENTIFIER}`
  const oldKeys = Object.keys(localStorage).filter(
    (key) => key.startsWith('nebvla-auth') && key !== currentKey,
  )

  if (oldKeys.length > 0) {
    oldKeys.forEach((key) => localStorage.removeItem(key))
  }
}

// Executar limpeza na inicialização
cleanupOldTokens()

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      activeOrganizationId: readInitialActiveOrg(),
      memberships: [],
      orgChoiceRequired: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const loginResponse = await api.post('/api/v1/auth/login/json', {
            email,
            password,
            trusted_device_token: getTrustedDeviceToken() || undefined,
          })

          // Check if OTP is required
          if (loginResponse.data.requires_otp) {
            set({ isLoading: false })
            throw new OTPRequiredError(
              loginResponse.data.temp_token,
              loginResponse.data.message,
              loginResponse.data.method || 'email',
              !!loginResponse.data.email_fallback,
            )
          }

          const token = loginResponse.data.access_token
          const refreshToken = loginResponse.data.refresh_token

          const {
            user: userData,
            activeOrgId,
            orgChoiceRequired,
            memberships,
          } = await carregarSessao(token)
          writeSessionActiveOrg(activeOrgId)

          set({
            token: token,
            refreshToken: refreshToken,
            user: userData,
            isAuthenticated: true,
            isLoading: false,
            activeOrganizationId: activeOrgId,
            memberships: memberships,
            orgChoiceRequired,
          })
        } catch (error) {
          if (error instanceof OTPRequiredError) {
            throw error
          }
          set({
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            activeOrganizationId: null,
            memberships: [],
            orgChoiceRequired: false,
          })
          throw error
        }
      },

      completeOTPLogin: async (accessToken: string, refreshToken?: string) => {
        set({ isLoading: true })
        try {
          const {
            user: userData,
            activeOrgId,
            orgChoiceRequired,
            memberships,
          } = await carregarSessao(accessToken)
          writeSessionActiveOrg(activeOrgId)

          set({
            token: accessToken,
            refreshToken: refreshToken ?? get().refreshToken,
            user: userData,
            isAuthenticated: true,
            isLoading: false,
            activeOrganizationId: activeOrgId,
            memberships: memberships,
            orgChoiceRequired,
          })
        } catch (error) {
          set({
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            activeOrganizationId: null,
            memberships: [],
            orgChoiceRequired: false,
          })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true })
        try {
          const response = await api.post('/api/v1/auth/register', data)

          set({ isLoading: false })

          return response.data
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      checkAuth: async () => {
        const { token, user, activeOrganizationId } = get()

        if (token) {
          try {
            // Se já temos user no cache, libera a UI imediatamente
            // e re-fetcha /me em background para atualizar permissions
            if (user) {
              set({ isAuthenticated: true, isLoading: false })
            }

            const {
              user: userData,
              activeOrgId: orgId,
              orgChoiceRequired,
              memberships,
            } = await carregarSessao(token, activeOrganizationId)

            set({
              user: userData,
              isAuthenticated: true,
              isLoading: false,
              activeOrganizationId: orgId,
              memberships: memberships,
              orgChoiceRequired,
            })

            if (orgId) writeSessionActiveOrg(orgId)
            stripOrgFromUrl()
          } catch {
            get().logout()
          }
        } else {
          set({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            activeOrganizationId: null,
            memberships: [],
            orgChoiceRequired: false,
          })
        }
      },

      logout: () => {
        writeSessionActiveOrg(null)
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          activeOrganizationId: null,
          memberships: [],
          orgChoiceRequired: false,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      updateUser: (user: User) => {
        set({ user })
      },

      setActiveOrganization: (orgId: string) => {
        const { memberships, user } = get()
        const membership = memberships.find((m) => m.organization_id === orgId)
        if (!membership) return

        // Update active org id and user's organization view
        const updatedUser = user
          ? {
              ...user,
              organization_id: orgId,
              organization: {
                id: orgId,
                name: membership.organization_name,
                type: membership.organization_type as any,
                parent_id: membership.parent_id,
                parent_name: membership.parent_name,
              },
            }
          : null

        writeSessionActiveOrg(orgId)
        set({
          activeOrganizationId: orgId,
          user: updatedUser,
          orgChoiceRequired: false,
        })
      },

      chooseOrganization: async (orgId: string) => {
        get().setActiveOrganization(orgId)
        // Sem header, o /auth/me trouxe roles/permissions do topo vazios: relê agora que
        // o X-Organization-Id sai, senão telas que leem user.permissions só se corrigem no F5.
        await get().checkAuth()
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading })
      },

      setTokens: (accessToken: string, refreshToken?: string) => {
        set((state) => ({
          token: accessToken,
          refreshToken: refreshToken ?? state.refreshToken,
          isAuthenticated: true,
        }))
      },
    }),
    {
      name: `nebvla-auth-${APP_IDENTIFIER}`,
      // activeOrganizationId NÃO é persistido aqui: é por-aba (sessionStorage).
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        memberships: state.memberships,
      }),
      // Reaplica a prioridade por-aba na reidratação: ?org= / sessionStorage
      // vencem o valor legado que ainda exista no blob do localStorage.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<AuthState>) }
        // blob de versão antiga pode trazer a org ativa: nunca vale como escolha (#1145)
        merged.activeOrganizationId = current.activeOrganizationId
        const override = pickPerTabOrgOverride(merged.memberships || [], {
          urlOrg: readOrgFromUrl(),
          sessionOrg: readSessionActiveOrg(),
        })
        if (override) {
          merged.activeOrganizationId = override
        }
        // F5 não repergunta (sessionStorage); aba nova repergunta.
        merged.orgChoiceRequired =
          !!merged.token && needsOrgChoice(merged.memberships || [], readPerTabSignals())
        return merged
      },
      onRehydrateStorage: () => (state) => {
        // Set loading to false after rehydration
        if (state) {
          state.setLoading(false)
        }
      },
    },
  ),
)

// Configure API auth handlers after store creation
setAuthHandlers(
  () => useAuthStore.getState().token,
  () => useAuthStore.getState().logout(),
  () => useAuthStore.getState().activeOrganizationId,
  () => useAuthStore.getState().refreshToken,
  (accessToken: string, refreshToken?: string) =>
    useAuthStore.getState().setTokens(accessToken, refreshToken),
)
